import { z } from "zod";
import {
  TopolBlockCssClassSchema,
  TopolBlockAlignSchema,
  TopolBlockSchema,
  TopolColorSchema,
  TopolColorBearingSchema,
} from "../commonSchema";

export const TopolRatingUrlModeSchema = z
  .enum(["pattern", "individual"])
  .catch("pattern");

export const TopolRatingSegmentModeSchema = z
  .enum(["text", "image"])
  .catch("text");

const TopolRatingSegmentSchema = z.object({
  tagName: z.literal("mj-rating-segment"),
  attributes: z
    .object({
      label: z.string().catch(""),
      src: z.string().trim().catch(""),
      alt: z.string().catch(""),
      href: z.string().trim().catch(""),
      value: z.union([z.string(), z.number()]).catch(""),
    })
    .default({
      label: "",
      src: "",
      alt: "",
      href: "",
      value: "",
    }),
});

export type TopolRatingSegment = z.infer<typeof TopolRatingSegmentSchema>;

const TopolRatingTextStyleSchema = z.object({
  color: TopolColorSchema.catch("#1a1a1a"),
  "font-size": z.string(),
  "font-family": z.string(),
  "line-height": z.union([z.string(), z.number()]).optional(),
  "font-weight": z.string().optional(),
  "font-style": z.string().optional(),
  "text-transform": z.string().optional(),
  "text-decoration": z.string().optional(),
  "letter-spacing": z.string().optional(),
});

const TopolRatingQuestionSchema = TopolRatingTextStyleSchema.extend({
  show: z.boolean().catch(true),
  padding: z.string().optional(),
}).default({
  show: true,
  color: "#1a1a1a",
  "font-size": "14px",
  "font-family": "Helvetica, Arial, sans-serif",
});

const TopolRatingCaptionLayoutSchema = z
  .enum(["below", "inline"])
  .catch("below");

const TopolRatingCaptionsSchema = TopolRatingTextStyleSchema.extend({
  show: z.boolean().catch(true),
  padding: z.string().optional(),
  layout: TopolRatingCaptionLayoutSchema,
  layoutMobile: TopolRatingCaptionLayoutSchema,
}).default({
  show: true,
  color: "#1a1a1a",
  "font-size": "14px",
  "font-family": "Helvetica, Arial, sans-serif",
  layout: "below",
  layoutMobile: "below",
});

const TopolRatingImageLabelSchema = TopolRatingTextStyleSchema.extend({
  show: z.boolean().catch(false),
}).default({
  show: false,
  color: "#1a1a1a",
  "font-size": "14px",
  "font-family": "Helvetica, Arial, sans-serif",
});

const TopolRatingSegmentsSchema = TopolRatingTextStyleSchema.extend({
  mode: TopolRatingSegmentModeSchema,
  start: z.coerce.number().catch(0),
  count: z.coerce.number().catch(11),
  size: z.string().catch("32px"),
  gap: z.string().catch("4px"),
  autoWidth: z.boolean().catch(true),
  "background-color": TopolColorSchema.catch("#ffffff"),
  border: TopolColorBearingSchema.catch("1px #1a1a1a solid"),
  "border-radius": z.string().catch("8px"),
}).default({
  mode: "text",
  start: 0,
  count: 11,
  size: "32px",
  gap: "4px",
  color: "#1a1a1a",
  "font-size": "14px",
  "font-family": "Helvetica, Arial, sans-serif",
  "background-color": "#ffffff",
  border: "1px solid #1a1a1a",
  "border-radius": "8px",
  autoWidth: true,
});

const TopolRatingUrlSchema = z
  .object({
    mode: TopolRatingUrlModeSchema,
  })
  .default({
    mode: "pattern",
  });

const TopolRatingSegmentsHoverSchema = z
  .object({
    "background-color": TopolColorSchema.optional().catch(undefined),
    color: TopolColorSchema.optional().catch(undefined),
    border: TopolColorBearingSchema.optional(),
  })
  .optional()
  .catch(undefined);

export const TopolRatingBlockSchema = TopolBlockSchema.extend({
  tagName: z.literal("mj-rating"),
  attributes: z
    .object({
      align: TopolBlockAlignSchema.catch("center"),
      padding: z.string().catch("10px 10px 10px 10px"),
      containerWidth: z.number().catch(600),
      "container-background-color":
        TopolColorSchema.optional().catch(undefined),
      "css-class": TopolBlockCssClassSchema.optional().catch(undefined),
    })
    .default({
      align: "center",
      padding: "10px 10px 10px 10px",
      containerWidth: 600,
    }),
  questionAttributes: TopolRatingQuestionSchema,
  questionText: z.string().catch(""),
  captionsAttributes: TopolRatingCaptionsSchema,
  captionTextLeft: z.string().catch(""),
  captionTextRight: z.string().catch(""),
  segmentsAttributes: TopolRatingSegmentsSchema,
  segmentsHover: TopolRatingSegmentsHoverSchema,
  imageLabelAttributes: TopolRatingImageLabelSchema,
  url: TopolRatingUrlSchema,
  urlPattern: z.string().catch(""),
  children: z.array(TopolRatingSegmentSchema).default([]),
});

export type TopolRatingBlock = z.infer<typeof TopolRatingBlockSchema>;
